"use client";

/**
 * ScheduleComponent
 * Schedule/agenda section for landing pages with grouped display by date
 */

import { LandingPageComponent, ScheduleConfig, ScheduleItem } from "@/lib/database/landing-pages";
import { DEFAULT_VALUES } from "../../../_logic/constants";
import { generateColorPalette, DEFAULT_PRIMARY_COLOR } from "../../../_logic/color-utils";
import { formatDateLocal } from "@/lib/utils/date-utils";
import { formatTime, formatDateShort, formatDayOfWeek } from "@/lib/utils/formatting";
import { useState, useEffect, useMemo, useCallback } from "react";
import { useSearchParams, useRouter, usePathname } from "next/navigation";
import { User, ChevronDown, MapPin, ChevronLeft, ChevronRight, Award } from "lucide-react";

interface ScheduleComponentProps {
  component: LandingPageComponent;
  eventId: number;
  primaryColor?: string;
}

// Animation constants
const ANIMATION = {
  fast: "duration-300",
  medium: "duration-500",
} as const;

// Helper functions
const hasValue = (value?: string): boolean => Boolean(value?.trim());

// Helper function to convert markdown-style links and URLs to clickable links
const convertUrlsToLinks = (text: string) => {
  // First, handle markdown-style links [text](url)
  // Updated regex to be more flexible with URLs and whitespace
  const markdownLinkRegex = /\[([^\]]+)\]\s*\(\s*(https?:\/\/[^)\s]+)\s*\)/g;
  const parts: (string | JSX.Element)[] = [];
  let lastIndex = 0;
  let match;

  while ((match = markdownLinkRegex.exec(text)) !== null) {
    // Add text before the link
    if (match.index > lastIndex) {
      const beforeText = text.slice(lastIndex, match.index);
      parts.push(...convertRawUrls(beforeText));
    }

    // Add the markdown link
    parts.push(
      <a
        key={`md-${match.index}`}
        href={match[2].trim()}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 hover:underline"
        onClick={(e) => e.stopPropagation()}
      >
        {match[1]}
      </a>
    );

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    const remainingText = text.slice(lastIndex);
    parts.push(...convertRawUrls(remainingText));
  }

  return parts;
};

// Helper to convert raw URLs in text
const convertRawUrls = (text: string): (string | JSX.Element)[] => {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = text.split(urlRegex);

  return parts.map((part, i) =>
    urlRegex.test(part) ? (
      <a
        key={`url-${i}`}
        href={part}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 hover:underline"
        onClick={(e) => e.stopPropagation()}
      >
        {part}
      </a>
    ) : (
      part
    )
  );
};

// Shared component for schedule item details
interface DisplayValues {
  title?: string;
  speaker?: string;
  location?: string;
}

interface ScheduleItemDetailsProps {
  item: ScheduleItem;
  displayValues: DisplayValues;
  variant: "cards" | "carousel" | "accordion";
}

const ScheduleItemDetails = ({ item, displayValues, variant }: ScheduleItemDetailsProps) => {
  const isCards = variant === "cards";
  const textSize = isCards ? "text-xs" : "text-sm";
  const iconSize = isCards ? "w-3 h-3" : "w-4 h-4";
  const showLabels = variant !== "cards";

  return (
    <>
      {hasValue(displayValues.speaker) && (
        <p
          className={`${textSize} text-gray-600 ${isCards ? "mt-1" : "mb-1"} flex items-center ${isCards ? "gap-1" : "gap-2"}`}
        >
          <User className={iconSize} />
          {showLabels && <strong className="text-gray-900">Speaker:</strong>}{" "}
          {convertUrlsToLinks(displayValues.speaker!)}
        </p>
      )}

      {hasValue(displayValues.location) && (
        <p
          className={`${textSize} text-gray-600 ${isCards ? "mt-1" : "mb-1"} flex items-center ${isCards ? "gap-1" : "gap-2"}`}
        >
          <MapPin className={iconSize} />
          {showLabels && <strong className="text-gray-900">Location:</strong>}{" "}
          {convertUrlsToLinks(displayValues.location!)}
        </p>
      )}

      {hasValue(item.vendor) && (
        <p
          className={`${textSize} text-gray-600 ${isCards ? "mt-1" : variant === "carousel" ? "" : "mb-2"} flex items-center ${isCards ? "gap-1" : "gap-2"}`}
        >
          <Award className={iconSize} />
          {showLabels ? (
            <span>
              <strong className="text-gray-900">Sponsor:</strong>{" "}
              <span className="inline-block bg-gray-200 text-gray-800 text-xs font-medium px-2 py-1 rounded-full">
                {convertUrlsToLinks(item.vendor!)}
              </span>
            </span>
          ) : (
            convertUrlsToLinks(item.vendor!)
          )}
        </p>
      )}
    </>
  );
};

export default function ScheduleComponent({ component, eventId, primaryColor }: ScheduleComponentProps) {
  // Config
  const config = (component.config || {}) as ScheduleConfig;

  // URL params for role-based filtering
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();
  const urlRole = searchParams.get("role") || searchParams.get("tag");

  // Color palette
  const palette = useMemo(() => generateColorPalette(primaryColor || DEFAULT_PRIMARY_COLOR), [primaryColor]);

  // Constants
  const title = config.title || DEFAULT_VALUES.SCHEDULE_TITLE;
  const displayStyle = config.displayStyle || "accordion";

  // States
  const [expandedDates, setExpandedDates] = useState<Set<string>>(new Set());
  const [currentSlide, setCurrentSlide] = useState(0);
  const [selectedItem, setSelectedItem] = useState<ScheduleItem | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<string | null>(null);
  const [cyoaChoices, setCyoaChoices] = useState<Record<string, string>>({});

  // Use available roles from config (set in the editor)
  const availableRoles = config.availableRoles || [];

  // Initialize selected role from URL param
  useEffect(() => {
    if (urlRole && availableRoles.includes(urlRole)) {
      setSelectedRole(urlRole);
    }
  }, [urlRole, availableRoles]);

  // Helper to get display values for an item based on selected role
  const getDisplayValues = useCallback(
    (item: ScheduleItem) => {
      // If a role is selected and there's an override for it, use the override values
      if (selectedRole && item.roleOverrides?.[selectedRole]) {
        const override = item.roleOverrides[selectedRole];
        return {
          title: override.title || item.title,
          speaker: override.speaker || item.speaker,
          location: override.location || item.location,
        };
      }
      // Otherwise use the general values
      return {
        title: item.title,
        speaker: item.speaker,
        location: item.location,
      };
    },
    [selectedRole]
  );

  /** Get the CYOA options for this item given the current selected role. Returns null if CYOA not active. */
  const getCYOAOptions = useCallback(
    (item: ScheduleItem): string[] | null => {
      if (!item.cyoaConfig || !selectedRole) return null;
      const options = item.cyoaConfig[selectedRole];
      return options && options.length > 0 ? options : null;
    },
    [selectedRole]
  );

  /** Get display values respecting per-item CYOA selections. Defaults to first option when CYOA is active. */
  const getCYOADisplayValues = useCallback(
    (item: ScheduleItem) => {
      const options = getCYOAOptions(item);
      if (options) {
        const itemKey = `${item.date}_${item.startTime}`;
        const cyoaRole = cyoaChoices[itemKey] || options[0];
        if (cyoaRole && item.roleOverrides?.[cyoaRole]) {
          const override = item.roleOverrides[cyoaRole];
          return {
            title: override.title || item.title,
            speaker: override.speaker || item.speaker,
            location: override.location || item.location,
          };
        }
      }
      return getDisplayValues(item);
    },
    [cyoaChoices, getDisplayValues, getCYOAOptions]
  );

  /** Handle CYOA choice (local state only on public page). */
  const handleCYOAChoice = useCallback((itemKey: string, role: string) => {
    setCyoaChoices((prev) => ({ ...prev, [itemKey]: role }));
  }, []);

  /** Render inline CYOA pills for a schedule item. */
  const renderCYOAPills = useCallback(
    (item: ScheduleItem) => {
      const options = getCYOAOptions(item);
      if (!options) return null;
      const itemKey = `${item.date}_${item.startTime}`;
      const activeRole =
        cyoaChoices[itemKey] || (selectedRole && options.includes(selectedRole) ? selectedRole : options[0]);

      return (
        <div className="flex flex-wrap gap-1 mt-1 mb-1">
          {options.map((role) => (
            <button
              key={role}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleCYOAChoice(itemKey, role);
              }}
              className={`px-2 py-0.5 rounded-full text-xs font-medium transition-all ${
                activeRole === role ? "text-white shadow-sm" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
              style={activeRole === role ? { backgroundColor: palette.primary } : {}}
            >
              {role}
            </button>
          ))}
        </div>
      );
    },
    [cyoaChoices, selectedRole, palette.primary, handleCYOAChoice, getCYOAOptions]
  );

  // Helper to get combined time range for grouped items
  const getCombinedTimeRange = useCallback((items: ScheduleItem[]) => {
    if (items.length === 0) return { startTime: "", endTime: "" };
    if (items.length === 1) {
      return { startTime: items[0].startTime || "", endTime: items[0].endTime || "" };
    }

    // Find earliest start time and latest end time
    let earliestStart = items[0].startTime || "";
    let latestEnd = items[0].endTime || "";

    items.forEach((item) => {
      const startTime = item.startTime || "";
      const endTime = item.endTime || "";
      if (startTime && (!earliestStart || startTime < earliestStart)) {
        earliestStart = startTime;
      }
      if (endTime && (!latestEnd || endTime > latestEnd)) {
        latestEnd = endTime;
      }
    });

    return { startTime: earliestStart, endTime: latestEnd };
  }, []);

  // Filter items - all items are shown, role just changes display content
  const items = useMemo(() => {
    return (config.items || []).filter(
      (item) => hasValue(item.date) && hasValue(item.startTime) && hasValue(item.title)
    );
  }, [config.items]);

  // Group items by date, then by title (items with role overrides are shown separately)
  const groupedByDate = useMemo(() => {
    // First group by date
    const byDate = items.reduce(
      (acc, item) => {
        const dateKey = item.date!; // Safe after filter
        if (!acc[dateKey]) {
          acc[dateKey] = [];
        }
        acc[dateKey].push(item);
        return acc;
      },
      {} as Record<string, ScheduleItem[]>
    );

    // Then within each date, group by title (but separate items with role overrides)
    const result: Record<
      string,
      Array<{
        type: "grouped" | "single";
        items: ScheduleItem[];
        title: string;
      }>
    > = {};

    Object.entries(byDate).forEach(([date, dateItems]) => {
      // Sort all items by start time first
      const sorted = [...dateItems].map((item) => ({
        item,
        displayTitle: getCYOADisplayValues(item).title || "",
      }));
      sorted.sort((a, b) => (a.item.startTime || "").localeCompare(b.item.startTime || ""));

      // Group consecutive items with the same display title
      const displayItems: Array<{
        type: "grouped" | "single";
        items: ScheduleItem[];
        title: string;
      }> = [];

      sorted.forEach(({ item, displayTitle }) => {
        const lastGroup = displayItems[displayItems.length - 1];
        if (lastGroup && lastGroup.title === displayTitle) {
          // Same title as previous item — merge into existing group
          lastGroup.items.push(item);
          lastGroup.type = "grouped";
        } else {
          displayItems.push({ type: "single", items: [item], title: displayTitle });
        }
      });

      result[date] = displayItems;
    });

    return result;
  }, [items, getCYOADisplayValues]);

  const totalSlides = Object.keys(groupedByDate).length;

  // Effects
  // Reset carousel slide if out of bounds
  useEffect(() => {
    if (displayStyle === "carousel" && currentSlide >= totalSlides && totalSlides > 0) {
      setCurrentSlide(0);
    }
  }, [totalSlides, currentSlide, displayStyle]);

  // Handlers
  const toggleDate = useCallback((date: string) => {
    setExpandedDates((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(date)) {
        newSet.delete(date);
      } else {
        newSet.add(date);
      }
      return newSet;
    });
  }, []);

  const nextSlide = useCallback(() => {
    if (totalSlides > 0) {
      setCurrentSlide((prev) => (prev + 1) % totalSlides);
    }
  }, [totalSlides]);

  const prevSlide = useCallback(() => {
    if (totalSlides > 0) {
      setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
    }
  }, [totalSlides]);

  const goToSlide = useCallback((index: number) => {
    setCurrentSlide(index);
  }, []);

  const openModal = useCallback((item: ScheduleItem) => {
    setSelectedItem(item);
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedItem(null);
  }, []);

  const selectRole = useCallback(
    (role: string | null) => {
      setSelectedRole(role);

      // Update URL with new role parameter, preserving hash
      const params = new URLSearchParams(searchParams.toString());
      if (role) {
        params.set("role", role);
      } else {
        params.delete("role");
        params.delete("tag"); // Also remove legacy tag param
      }
      const hash = typeof window !== "undefined" ? window.location.hash : "";
      const queryString = params.toString() ? `?${params.toString()}` : "";
      const newUrl = `${pathname}${queryString}${hash}`;
      router.replace(newUrl, { scroll: false });
    },
    [searchParams, pathname, router]
  );

  // Keyboard navigation for carousel
  useEffect(() => {
    if (displayStyle !== "carousel") return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") {
        prevSlide();
      } else if (e.key === "ArrowRight") {
        nextSlide();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [displayStyle, prevSlide, nextSlide]);

  // Render helpers
  const renderEmptyState = () => <p className="text-gray-500 text-center">No schedule items configured yet.</p>;

  const renderRoleSelector = () => {
    if (availableRoles.length === 0) return null;

    return (
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <span className="text-sm font-medium text-gray-700">View schedule for:</span>

          {/* Role buttons */}
          <div className="flex flex-wrap gap-2">
            {/* All button */}
            <button
              type="button"
              onClick={() => selectRole(null)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all touch-manipulation ${
                !selectedRole ? "text-white shadow-md" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
              style={!selectedRole ? { backgroundColor: palette.primary } : {}}
              aria-pressed={!selectedRole}
            >
              All
            </button>

            {/* Role buttons */}
            {availableRoles.map((role) => (
              <button
                type="button"
                key={role}
                onClick={() => selectRole(role)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all touch-manipulation ${
                  selectedRole === role ? "text-white shadow-md" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
                style={selectedRole === role ? { backgroundColor: palette.primary } : {}}
                aria-pressed={selectedRole === role}
              >
                {role}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderCardsView = () => (
    <div className="max-w-7xl mx-auto">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {Object.entries(groupedByDate).map(([date, dateItems]) => {
          const dateObj = new Date(date + "T00:00:00");
          return (
            <div key={date} className="border border-gray-200 rounded-lg p-4 bg-white">
              {/* Date header */}
              <div className="flex justify-between items-start mb-3">
                <div>
                  <div className="text-lg font-semibold text-gray-800">
                    {dateObj.toLocaleDateString("en-US", { weekday: "short" })}
                  </div>
                  <div className="text-sm text-gray-600">
                    {dateObj.toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                  </div>
                </div>
                <div className="text-xs text-indigo-600 font-medium">
                  {dateItems.reduce((sum, group) => sum + group.items.length, 0)} sessions
                </div>
              </div>

              {/* Session items */}
              <div className="space-y-2">
                {dateItems.map((group, groupIndex) => {
                  if (group.type === "grouped") {
                    // Grouped items: show as single block with combined time range
                    const combinedTime = getCombinedTimeRange(group.items);
                    const firstItem = group.items[0];
                    const displayValues = getCYOADisplayValues(firstItem);
                    const hasDetails =
                      hasValue(displayValues.speaker) || hasValue(displayValues.location) || hasValue(firstItem.vendor);
                    const itemKey = `group-${date}-${groupIndex}`;

                    return hasDetails ? (
                      <button
                        type="button"
                        key={itemKey}
                        onClick={() => openModal(firstItem)}
                        className="bg-indigo-50 rounded p-2 text-xs text-left w-full cursor-pointer hover:bg-indigo-100 transition-colors touch-manipulation"
                      >
                        <div className="font-medium text-indigo-900 mb-1">{group.title}</div>
                        {renderCYOAPills(firstItem)}
                        <div className="text-indigo-600">
                          {formatTime(combinedTime.startTime)}
                          {hasValue(combinedTime.endTime) && ` - ${formatTime(combinedTime.endTime)}`}
                        </div>
                        <div className="text-xs text-indigo-400 mt-1">Click for details →</div>
                      </button>
                    ) : (
                      <div key={itemKey} className="bg-indigo-50 rounded p-2 text-xs">
                        <div className="font-medium text-indigo-900 mb-1">{group.title}</div>
                        {renderCYOAPills(firstItem)}
                        <div className="text-indigo-600">
                          {formatTime(combinedTime.startTime)}
                          {hasValue(combinedTime.endTime) && ` - ${formatTime(combinedTime.endTime)}`}
                        </div>
                      </div>
                    );
                  } else {
                    // Single item
                    const item = group.items[0];
                    const itemKey = `${date}-${item.startTime}-${groupIndex}`;
                    const displayValues = getCYOADisplayValues(item);
                    const hasDetails =
                      hasValue(displayValues.speaker) || hasValue(displayValues.location) || hasValue(item.vendor);
                    return hasDetails ? (
                      <button
                        type="button"
                        key={itemKey}
                        onClick={() => openModal(item)}
                        className="bg-indigo-50 rounded p-2 text-xs text-left w-full cursor-pointer hover:bg-indigo-100 transition-colors touch-manipulation"
                      >
                        <div className="font-medium text-indigo-900 mb-1">{displayValues.title}</div>
                        {renderCYOAPills(item)}
                        <div className="text-indigo-600">
                          {formatTime(item.startTime!)}
                          {hasValue(item.endTime) && ` - ${formatTime(item.endTime!)}`}
                        </div>
                        <div className="text-xs text-indigo-400 mt-1">Click for details →</div>
                      </button>
                    ) : (
                      <div key={itemKey} className="bg-indigo-50 rounded p-2 text-xs">
                        <div className="font-medium text-indigo-900 mb-1">{displayValues.title}</div>
                        {renderCYOAPills(item)}
                        <div className="text-indigo-600">
                          {formatTime(item.startTime!)}
                          {hasValue(item.endTime) && ` - ${formatTime(item.endTime!)}`}
                        </div>
                      </div>
                    );
                  }
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderCarouselView = () => {
    if (totalSlides === 0) return renderEmptyState();

    return (
      <div className="max-w-4xl mx-auto">
        <div className="relative">
          {/* Carousel Content */}
          <div className="overflow-hidden">
            {Object.entries(groupedByDate).map(([date, dateItems], index) => (
              <div
                key={date}
                className={`transition-opacity ${ANIMATION.medium} ${
                  index === currentSlide ? "opacity-100" : "opacity-0 absolute inset-0 pointer-events-none"
                }`}
                role="tabpanel"
                aria-hidden={index !== currentSlide}
              >
                {/* Day Header with Navigation */}
                <div className="flex items-center justify-center gap-8 mb-8">
                  {/* Previous Button */}
                  {totalSlides > 1 && (
                    <button
                      type="button"
                      onClick={prevSlide}
                      className="bg-white rounded-lg p-3 shadow-md hover:bg-gray-50 transition-colors flex-shrink-0 touch-manipulation"
                      aria-label="Previous day"
                    >
                      <ChevronLeft className="w-6 h-6 text-gray-700" />
                    </button>
                  )}

                  {/* Day Title */}
                  <div className="text-center">
                    <h2 className="text-4xl font-bold text-gray-900 mb-2">{formatDayOfWeek(date)}</h2>
                    <p className="text-xl text-gray-600">{formatDateShort(date)}</p>
                  </div>

                  {/* Next Button */}
                  {totalSlides > 1 && (
                    <button
                      type="button"
                      onClick={nextSlide}
                      className="bg-white rounded-lg p-3 shadow-md hover:bg-gray-50 transition-colors flex-shrink-0 touch-manipulation"
                      aria-label="Next day"
                    >
                      <ChevronRight className="w-6 h-6 text-gray-700" />
                    </button>
                  )}
                </div>

                {/* Schedule Items */}
                <div className="bg-white rounded-2xl shadow-md border border-gray-200 p-8 space-y-6">
                  {dateItems.map((group, groupIndex) => {
                    if (group.type === "grouped") {
                      // Grouped items: show as single block with combined time range
                      const combinedTime = getCombinedTimeRange(group.items);
                      const firstItem = group.items[0];
                      const displayValues = getCYOADisplayValues(firstItem);
                      const itemKey = `group-${date}-${groupIndex}`;

                      return (
                        <div
                          key={itemKey}
                          className="flex flex-col sm:flex-row sm:items-start gap-4 pb-6 border-b border-gray-100 last:border-b-0 last:pb-0"
                        >
                          {/* Time */}
                          <div className="flex-shrink-0 sm:w-32">
                            <span
                              className={`inline-block text-sm font-semibold text-white text-center px-4 py-2 leading-tight ${
                                hasValue(combinedTime.endTime) ? "rounded-2xl" : "rounded-full"
                              }`}
                              style={{ backgroundColor: palette.primary }}
                            >
                              {formatTime(combinedTime.startTime)}
                              {hasValue(combinedTime.endTime) && (
                                <>
                                  <br />–<br />
                                  {formatTime(combinedTime.endTime)}
                                </>
                              )}
                            </span>
                          </div>

                          {/* Details */}
                          <div className="flex-1">
                            <h3 className="text-lg font-bold text-gray-900 mb-2">{displayValues.title}</h3>
                            {renderCYOAPills(firstItem)}
                            <ScheduleItemDetails item={firstItem} displayValues={displayValues} variant="carousel" />
                          </div>
                        </div>
                      );
                    } else {
                      // Single item
                      const item = group.items[0];
                      const itemKey = `${date}-${item.startTime}-${groupIndex}`;
                      const displayValues = getCYOADisplayValues(item);
                      return (
                        <div
                          key={itemKey}
                          className="flex flex-col sm:flex-row sm:items-start gap-4 pb-6 border-b border-gray-100 last:border-b-0 last:pb-0"
                        >
                          {/* Time */}
                          <div className="flex-shrink-0 sm:w-32">
                            <span
                              className={`inline-block text-sm font-semibold text-white text-center px-4 py-2 leading-tight ${
                                hasValue(item.endTime) ? "rounded-2xl" : "rounded-full"
                              }`}
                              style={{ backgroundColor: palette.primary }}
                            >
                              {formatTime(item.startTime!)}
                              {hasValue(item.endTime) && (
                                <>
                                  <br />–<br />
                                  {formatTime(item.endTime!)}
                                </>
                              )}
                            </span>
                          </div>

                          {/* Details */}
                          <div className="flex-1">
                            <h3 className="text-lg font-bold text-gray-900 mb-2">{displayValues.title}</h3>
                            {renderCYOAPills(item)}
                            <ScheduleItemDetails item={item} displayValues={displayValues} variant="carousel" />
                          </div>
                        </div>
                      );
                    }
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Dot Indicators */}
        {totalSlides > 1 && (
          <div className="flex justify-center gap-2 mt-8" role="tablist" aria-label="Schedule days">
            {Object.keys(groupedByDate).map((_, index) => (
              <button
                type="button"
                key={index}
                onClick={() => goToSlide(index)}
                className={`w-3 h-3 rounded-full transition-all touch-manipulation ${
                  index === currentSlide ? "w-8" : "bg-gray-300 hover:bg-gray-400"
                }`}
                style={index === currentSlide ? { backgroundColor: palette.primary } : {}}
                role="tab"
                aria-label={`Go to day ${index + 1}`}
                aria-selected={index === currentSlide}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderAccordionView = () => (
    <div className="max-w-5xl mx-auto space-y-6">
      {Object.entries(groupedByDate).map(([date, dateItems]) => {
        const isExpanded = expandedDates.has(date);
        const sectionId = `schedule-${date}`;

        return (
          <div key={date} className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
            {/* Date Header - Collapsible */}
            <button
              type="button"
              onClick={() => toggleDate(date)}
              className="w-full p-6 bg-white hover:bg-gray-50 text-left flex justify-between items-center transition-colors touch-manipulation"
              aria-expanded={isExpanded}
              aria-controls={sectionId}
            >
              <h3 className="text-2xl font-semibold text-gray-900">{formatDateLocal(date, "dddd, MMMM D, YYYY")}</h3>
              <ChevronDown className={`w-6 h-6 text-gray-400 transition-transform ${isExpanded ? "rotate-180" : ""}`} />
            </button>

            {/* Schedule Items for this Date */}
            {isExpanded && (
              <div id={sectionId} className="p-6 space-y-6 bg-gray-50">
                {dateItems.map((group, groupIndex) => {
                  if (group.type === "grouped") {
                    // Grouped items: show as single block with combined time range
                    const combinedTime = getCombinedTimeRange(group.items);
                    const firstItem = group.items[0];
                    const displayValues = getCYOADisplayValues(firstItem);
                    const itemKey = `group-${date}-${groupIndex}`;

                    return (
                      <div
                        key={itemKey}
                        className="bg-white rounded-lg p-6 border-l-4"
                        style={{ borderLeftColor: palette.primary }}
                      >
                        <div className="flex flex-col sm:flex-row sm:items-start gap-6">
                          {/* Time */}
                          <span
                            className={`inline-block text-sm font-semibold sm:w-36 flex-shrink-0 text-center text-white px-4 py-2 leading-tight ${
                              hasValue(combinedTime.endTime) ? "rounded-2xl" : "rounded-full"
                            }`}
                            style={{ backgroundColor: palette.primary }}
                          >
                            {formatTime(combinedTime.startTime)}
                            {hasValue(combinedTime.endTime) && (
                              <>
                                <br />–<br />
                                {formatTime(combinedTime.endTime)}
                              </>
                            )}
                          </span>

                          {/* Details */}
                          <div className="flex-1">
                            {/* Title */}
                            <h4 className="font-bold mb-3 text-lg text-gray-900">{displayValues.title}</h4>
                            {renderCYOAPills(firstItem)}
                            <ScheduleItemDetails item={firstItem} displayValues={displayValues} variant="accordion" />
                          </div>
                        </div>
                      </div>
                    );
                  } else {
                    // Single item
                    const item = group.items[0];
                    const itemKey = `${date}-${item.startTime}-${groupIndex}`;
                    const displayValues = getCYOADisplayValues(item);
                    return (
                      <div
                        key={itemKey}
                        className="bg-white rounded-lg p-6 border-l-4"
                        style={{ borderLeftColor: palette.primary }}
                      >
                        <div className="flex flex-col sm:flex-row sm:items-start gap-6">
                          {/* Time */}
                          <span
                            className={`inline-block text-sm font-semibold sm:w-36 flex-shrink-0 text-center text-white px-4 py-2 leading-tight ${
                              hasValue(item.endTime) ? "rounded-2xl" : "rounded-full"
                            }`}
                            style={{ backgroundColor: palette.primary }}
                          >
                            {formatTime(item.startTime!)}
                            {hasValue(item.endTime) && (
                              <>
                                <br />–<br />
                                {formatTime(item.endTime!)}
                              </>
                            )}
                          </span>

                          {/* Details */}
                          <div className="flex-1">
                            {/* Title */}
                            <h4 className="font-bold mb-3 text-lg text-gray-900">{displayValues.title}</h4>
                            {renderCYOAPills(item)}
                            <ScheduleItemDetails item={item} displayValues={displayValues} variant="accordion" />
                          </div>
                        </div>
                      </div>
                    );
                  }
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  const renderScheduleView = () => {
    if (items.length === 0) return renderEmptyState();

    switch (displayStyle) {
      case "cards":
        return renderCardsView();
      case "carousel":
        return renderCarouselView();
      default:
        return renderAccordionView();
    }
  };

  return (
    <div id="schedule" className={displayStyle === "cards" ? "bg-gray-50 py-16" : "bg-white py-16"}>
      <div className="max-w-7xl mx-auto px-4">
        {displayStyle !== "carousel" && <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">{title}</h2>}
        {renderRoleSelector()}
        {renderScheduleView()}
      </div>

      {/* Detail Modal */}
      {isModalOpen &&
        selectedItem &&
        (() => {
          const modalDisplayValues = getCYOADisplayValues(selectedItem);
          return (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              {/* Backdrop button for closing */}
              <button
                type="button"
                className="absolute inset-0 bg-black bg-opacity-50 touch-manipulation"
                onClick={closeModal}
                aria-label="Close modal"
                tabIndex={-1}
              />
              <div className="relative bg-white rounded-lg p-6 max-w-lg w-full shadow-xl">
                {/* Close Button */}
                <button
                  type="button"
                  onClick={closeModal}
                  className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 touch-manipulation"
                  aria-label="Close"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>

                {/* Title */}
                <h3 className="text-2xl font-bold text-gray-900 mb-4 pr-8">{modalDisplayValues.title}</h3>
                {renderCYOAPills(selectedItem)}

                {/* Time */}
                <div className="mb-4">
                  <span
                    className="inline-block text-sm font-semibold text-white px-4 py-2 rounded-lg"
                    style={{ backgroundColor: palette.primary }}
                  >
                    {formatTime(selectedItem.startTime!)}
                    {hasValue(selectedItem.endTime) && ` - ${formatTime(selectedItem.endTime!)}`}
                  </span>
                </div>

                {/* Details */}
                <div className="space-y-3">
                  {hasValue(modalDisplayValues.speaker) && (
                    <div className="flex items-start gap-3">
                      <User className="w-5 h-5 text-gray-600 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-gray-700">Speaker</p>
                        <p className="text-base text-gray-900">{convertUrlsToLinks(modalDisplayValues.speaker!)}</p>
                      </div>
                    </div>
                  )}

                  {hasValue(modalDisplayValues.location) && (
                    <div className="flex items-start gap-3">
                      <MapPin className="w-5 h-5 text-gray-600 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-gray-700">Location</p>
                        <p className="text-base text-gray-900">{convertUrlsToLinks(modalDisplayValues.location!)}</p>
                      </div>
                    </div>
                  )}

                  {hasValue(selectedItem.vendor) && (
                    <div className="flex items-start gap-3">
                      <Award className="w-5 h-5 text-gray-600 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-gray-700">Sponsor</p>
                        <p className="text-base">
                          <span className="inline-block bg-gray-200 text-gray-800 text-sm font-medium px-3 py-1 rounded-full">
                            {convertUrlsToLinks(selectedItem.vendor!)}
                          </span>
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })()}
    </div>
  );
}
